/**
 * Product Types Registry
 *
 * Para agregar un nuevo tipo:
 * 1. Crear archivo en configs/
 * 2. Importar y registrar aquí
 */

import { config1100 } from './configs/1100-capital-protection';
import { config1130 } from './configs/1130-capital-protection-with-barrier';
import {
  config1140Express,
  config1140Fixed,
  config1140Floored,
  config1140Step,
} from './configs/1140-capital-certificate-protection-with-coupon';
import { config1200 } from './configs/1200-discount-certificate';
import {
  config1220Autocall,
  config1220Callable,
  config1220Standard,
} from './configs/1220-reverse-convertible-no-barrier';
import {
  config1230Autocall,
  config1230Callable,
  config1230Standard,
} from './configs/1230-barrier-reverse-convertible';
import {
  config1260Autocall,
  config1260Callable,
  config1260Standard,
  config1260Express,
} from './configs/1260-bonus-certificate';
import { config1300 } from './configs/1300-tracker';
import { config1310 } from './configs/1310-outperformance-certificate';
import { config1320Airbag, config1320Bonus, config1320BonusCertificate } from './configs/1320-bonus-outperformance';
import { config1410 } from './configs/1410-fixed-income-linked';
import { config2100 } from './configs/2100-warrant';
import { config2110 } from './configs/2110-spread-warrant';
import { config2200 } from './configs/2200-knock-out-warrant';
import { config2210 } from './configs/2210-mini-futures';
import { defaultConfig } from './configs/default';
import { ProductTypeConfig } from './types';

const productTypeRegistry = new Map<string, ProductTypeConfig>();
productTypeRegistry.set('1100', config1100);
productTypeRegistry.set('1130', config1130);
productTypeRegistry.set('1200', config1200);
productTypeRegistry.set(`1220-${config1220Autocall.callableType}`, config1220Autocall);
productTypeRegistry.set(`1220-${config1220Callable.callableType}`, config1220Callable);
productTypeRegistry.set(`1220-${config1220Standard.callableType}`, config1220Standard);
productTypeRegistry.set(`1230-${config1230Autocall.callableType}`, config1230Autocall);
productTypeRegistry.set(`1230-${config1230Callable.callableType}`, config1230Callable);
productTypeRegistry.set(`1230-${config1230Standard.callableType}`, config1230Standard);
productTypeRegistry.set(`1260-${config1260Autocall.callableType}`, config1260Autocall);
productTypeRegistry.set(`1260-${config1260Callable.callableType}`, config1260Callable);
productTypeRegistry.set(`1260-${config1260Standard.callableType}`, config1260Standard);
productTypeRegistry.set(`1260-${config1260Express.callableType}`, config1260Express);
productTypeRegistry.set('1300', config1300);
productTypeRegistry.set('1410', config1410);
productTypeRegistry.set('1310', config1310);
productTypeRegistry.set(`1320-${config1320Bonus.callableType}`, config1320Bonus);
productTypeRegistry.set(`1320-${config1320Airbag.callableType}`, config1320Airbag);
productTypeRegistry.set(`1320-${config1320BonusCertificate.callableType}`, config1320BonusCertificate);
productTypeRegistry.set('2100', config2100);
productTypeRegistry.set('2110', config2110);
productTypeRegistry.set('2200', config2200);
productTypeRegistry.set('2210', config2210);
productTypeRegistry.set(`1140-${config1140Express.callableType}`, config1140Express);
productTypeRegistry.set(`1140-${config1140Fixed.callableType}`, config1140Fixed);
productTypeRegistry.set(`1140-${config1140Floored.callableType}`, config1140Floored);
productTypeRegistry.set(`1140-${config1140Step.callableType}`, config1140Step);

/**
 * Extraer el tipo/variante del nombre del producto
 * Busca "Autocall", "Callable", "Bonus", "Airbag" o retorna "Standard" si no encuentra ninguno
 */
export const extractCallableType = (productName?: string): string | undefined => {
  if (!productName) return undefined;

  const name = productName.toLowerCase();

  // Buscar variantes de "Autocall" (case insensitive)
  if (name.includes('autocall')) {
    return 'Autocall';
  }

  // Buscar "Callable" (case insensitive)
  if (name.includes('callable')) {
    return 'Callable';
  }

  // Buscar "Express" (case insensitive)
  if (name.includes('express')) {
    return 'Express';
  }

  // Buscar "Fixed" (case insensitive)
  if (name.includes('fixed')) {
    return 'Fixed';
  }

  // Buscar "Floored" (case insensitive)
  if (name.includes('floored')) {
    return 'Floored';
  }

  // Buscar "Step" (case insensitive)
  if (name.includes('step')) {
    return 'Step';
  }

  // Buscar "Bonus" (case insensitive) - for 1320
  if (name.includes('bonus')) {
    return 'Bonus';
  }

  // Buscar "Airbag" (case insensitive) - for 1320
  if (name.includes('airbag')) {
    return 'Airbag';
  }

  // Si no encuentra ninguno, es Standard
  return 'Standard';
};

/**
 * Obtener configuración por código de producto y tipo de callable
 *
 * @param code - Código del producto (ej: '1220')
 * @param callableType - Tipo de callable extraído del nombre ('Autocall', 'Callable', 'Standard')
 * @returns Configuración del producto o defaultConfig si no se encuentra
 */
export const getProductConfig = (code?: string, callableType?: string): ProductTypeConfig => {
  if (!code) return defaultConfig;

  // Intentar buscar con callableType primero (clave compuesta: 'code-callableType')
  if (callableType) {
    const specificKey = `${code}-${callableType}`;
    const specificConfig = productTypeRegistry.get(specificKey);
    if (specificConfig) {
      return specificConfig;
    }
  }

  // Fallback 1: buscar solo con code
  const fallbackConfig = productTypeRegistry.get(code);
  if (fallbackConfig) {
    return fallbackConfig;
  }

  // Fallback 2: si callableType no era 'Standard', intentar con 'Standard'
  if (callableType && callableType !== 'Standard') {
    const standardKey = `${code}-Standard`;
    const standardConfig = productTypeRegistry.get(standardKey);
    if (standardConfig) {
      return standardConfig;
    }
  }

  return defaultConfig;
};

export * from './types';
export { defaultConfig };

